/**
 * ModeSelector Component
 *
 * Purpose: Allow users to choose between quick and full campaign creation modes
 * WHO: Campaign creators
 * WHAT: Toggle between quick 3-field creation and full multi-step setup
 * WHERE: Campaign Builder page (step 0, before main form)
 * WHY: Provide fast path for simple campaigns, full control for complex setups
 *
 * DECISION: Option C - Keep existing wizard, add quick mode toggle
 * - Quick mode: Name + Description + Visibility only
 * - Full mode: All 4-step wizard (current implementation)
 * - Both paths use same backend endpoint
 */

import React from "react";
import {
  VStack,
  HStack,
  Button,
  Heading,
  Text,
  Card,
  CardBody,
  Box,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { Zap, Settings } from "lucide-react";

interface ModeSelectorProps {
  isQuickMode: boolean;
  onModeChange: (isQuick: boolean) => void;
}

export const ModeSelector: React.FC<ModeSelectorProps> = ({
  isQuickMode,
  onModeChange,
}) => {
  return (
    <Box w="full">
      <VStack spacing={6} align="center">
        <VStack spacing={2} textAlign="center">
          <Heading size="lg">How would you like to create your campaign?</Heading>
          <Text color="gray.600">
            Choose a path that matches your needs. You can always add more details
            later.
          </Text>
        </VStack>

        <HStack spacing={4} w="full" maxW="800px" justify="center">
          {/* Quick Create Button */}
          <Card
            flex={1}
            cursor="pointer"
            bg={!isQuickMode ? "white" : "blue.50"}
            borderWidth={2}
            borderColor={!isQuickMode ? "gray.200" : "blue.500"}
            transition="all 0.2s"
            onClick={() => onModeChange(true)}
            _hover={{
              borderColor: "blue.500",
              boxShadow: "md",
            }}
            _dark={{
              bg: !isQuickMode ? "gray.800" : "blue.900",
              borderColor: !isQuickMode ? "gray.600" : "blue.400",
            }}
          >
            <CardBody>
              <VStack spacing={4} align="center" textAlign="center">
                <Box as={Zap} size={40} color="blue.500" />
                <VStack spacing={1}>
                  <Heading size="md">Quick Create</Heading>
                  <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
                    Get started fast
                  </Text>
                </VStack>

                <Divider />

                <VStack fontSize="sm" align="start" w="full" spacing={1}>
                  <Text>✓ Campaign name</Text>
                  <Text>✓ Description</Text>
                  <Text>✓ Visibility</Text>
                  <Text opacity={0.6}>~2 minutes</Text>
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Full Setup Button */}
          <Card
            flex={1}
            cursor="pointer"
            bg={isQuickMode ? "white" : "blue.50"}
            borderWidth={2}
            borderColor={isQuickMode ? "gray.200" : "blue.500"}
            transition="all 0.2s"
            onClick={() => onModeChange(false)}
            _hover={{
              borderColor: "blue.500",
              boxShadow: "md",
            }}
            _dark={{
              bg: isQuickMode ? "gray.800" : "blue.900",
              borderColor: isQuickMode ? "gray.600" : "blue.400",
            }}
          >
            <CardBody>
              <VStack spacing={4} align="center" textAlign="center">
                <Box as={Settings} size={40} color="orange.500" />
                <VStack spacing={1}>
                  <Heading size="md">Full Setup</Heading>
                  <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.400" }}>
                    Complete control
                  </Text>
                </VStack>

                <Divider />

                <VStack fontSize="sm" align="start" w="full" spacing={1}>
                  <Text>✓ All basic info</Text>
                  <Text>✓ World settings</Text>
                  <Text>✓ Adventure setup</Text>
                  <Text opacity={0.6}>~5 minutes</Text>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </HStack>
      </VStack>

      <Divider my={8} />
    </Box>
  );
};

export default ModeSelector;
